import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { MagnifyingGlass, Tag, ChatCircle, Heart, User, Shield, SignOut, LockKey, HandCoins, ShoppingCartSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";

const mobileTabs = [
  { to: "/browse", label: "Browse", icon: MagnifyingGlass, testId: "nav-browse" },
  { to: "/my-listings", label: "My Cards", icon: Tag, testId: "nav-mylistings", vendorOnly: true },
  { to: "/messages", label: "Msgs", icon: ChatCircle, testId: "nav-messages" },
  { to: "/transactions", label: "Sales", icon: HandCoins, testId: "nav-transactions" },
  { to: "/account", label: "Me", icon: User, testId: "nav-account" },
];

const desktopLinks = [
  { to: "/browse", label: "Browse", testId: "nav-browse-desktop" },
  { to: "/sell", label: "Sell Card", testId: "nav-sell-desktop", vendorOnly: true },
  { to: "/messages", label: "Messages", testId: "nav-messages-desktop" },
  { to: "/my-listings", label: "My Listings", testId: "nav-mylistings-desktop", vendorOnly: true },
  { to: "/transactions", label: "Sales", testId: "nav-transactions-desktop" },
  { to: "/offers", label: "Offers", testId: "nav-offers-desktop" },
  { to: "/watchlist", label: "Watchlist", testId: "nav-watchlist-desktop" },
  { to: "/account", label: "Account", testId: "nav-account-desktop" },
];

export default function AppNav() {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  // Offers "your-turn" count for the nav badge. Refetched on mount, on route
  // change, and every 60s so back-and-forth negotiations feel responsive
  // without hammering the server.
  const [offerTurnCount, setOfferTurnCount] = useState(0);
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get("/offers/counts");
        if (mounted) setOfferTurnCount(data.my_turn || 0);
      } catch {
        /* silently ignore — badge just stays hidden */
      }
    }
    if (user) {
      load();
      const id = setInterval(load, 60000);
      return () => { mounted = false; clearInterval(id); };
    }
  }, [user, location.pathname]);

  // Hide nav on landing/auth pages
  const hideOn = ["/", "/login", "/register", "/request-invite", "/how-it-works", "/terms", "/privacy", "/guidelines"];
  if (hideOn.includes(location.pathname)) return null;
  if (!user) return null;

  const isVendor = user.is_vendor || user.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      {/* Mobile compact top bar — logo + admin + bell + logout on one row. */}
      <header className="md:hidden sticky top-0 z-40 h-12 flex items-center justify-between border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-xl px-3">
        <NavLink to="/browse" className="flex items-center" data-testid="nav-logo-mobile">
          <Logo variant="sm" alt="Hunt57" />
        </NavLink>
        <div className="flex items-center gap-3">
          {user.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin-mobile" className="text-[#FFD700] flex items-center">
              <Shield size={20} weight="bold" />
            </NavLink>
          )}
          <NavLink to="/cart" data-testid="nav-cart-mobile" className="relative text-zinc-300 hover:text-white flex items-center" aria-label="Cart">
            <ShoppingCartSimple size={20} weight="bold" />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-[#FF5F1F] text-black text-[10px] font-bold flex items-center justify-center leading-none">
                {itemCount}
              </span>
            )}
          </NavLink>
          <NotificationBell />
          <button onClick={handleLogout} className="text-zinc-400 flex items-center" data-testid="logout-btn-mobile" aria-label="Log out">
            <SignOut size={20} weight="bold" />
          </button>
        </div>
      </header>

      {/* Desktop Top Nav */}
      <header className="hidden md:flex sticky top-0 z-40 h-16 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl px-6">
        <div className="flex items-center gap-10">
          <NavLink to="/browse" className="flex items-center" data-testid="nav-logo">
            <Logo variant="nav" alt="Hunt57" />
          </NavLink>
          <nav className="flex items-center gap-6">
            {desktopLinks.map((l) => {
              const locked = l.vendorOnly && !isVendor;
              const target = locked ? "/account?upgrade=1" : l.to;
              const showOffersBadge = l.to === "/offers" && offerTurnCount > 0;
              return (
                <NavLink
                  key={l.to}
                  to={target}
                  data-testid={l.testId}
                  title={locked ? "Vendor account required" : undefined}
                  className={({ isActive }) =>
                    `font-display uppercase text-xs tracking-widest font-bold transition-colors flex items-center gap-1 relative ${
                      isActive && !locked ? "text-[#FF5F1F]" : locked ? "text-zinc-600 hover:text-zinc-400" : "text-zinc-400 hover:text-white"
                    }`
                  }
                >
                  {locked && <LockKey size={11} weight="bold" />}
                  {l.label}
                  {showOffersBadge && (
                    <span
                      data-testid="nav-offers-badge"
                      className="ml-1 min-w-[16px] h-4 px-1 bg-[#FF5F1F] text-black text-[10px] font-bold flex items-center justify-center leading-none"
                    >
                      {offerTurnCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
            {user.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin-desktop"
                className={({ isActive }) =>
                  `font-display uppercase text-xs tracking-widest font-bold transition-colors flex items-center gap-1 ${
                    isActive ? "text-[#FFD700]" : "text-[#FFD700]/70 hover:text-[#FFD700]"
                  }`
                }
              >
                <Shield size={14} weight="bold" /> Admin
              </NavLink>
            )}
            {user.is_premium_advertiser && (
              <NavLink
                to="/my-ad"
                data-testid="nav-myad-desktop"
                className={({ isActive }) =>
                  `font-display uppercase text-xs tracking-widest font-bold transition-colors flex items-center gap-1 ${
                    isActive ? "text-[#FF5F1F]" : "text-[#FF5F1F]/70 hover:text-[#FF5F1F]"
                  }`
                }
              >
                My Ad
              </NavLink>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right hidden lg:block">
            <div className="font-display font-bold text-sm" data-testid="nav-user-name">{user.display_name || user.username}</div>
            <div className="font-mono text-[10px] text-zinc-500 uppercase">@{user.username}</div>
          </div>
          <NavLink to="/cart" data-testid="nav-cart-desktop" className="relative text-zinc-300 hover:text-white flex items-center" aria-label="Cart">
            <ShoppingCartSimple size={20} weight="bold" />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-[#FF5F1F] text-black text-[10px] font-bold flex items-center justify-center leading-none">
                {itemCount}
              </span>
            )}
          </NavLink>
          <NotificationBell />
          <button
            onClick={handleLogout}
            className="h57-btn-secondary text-xs py-2 px-4 flex items-center gap-2"
            data-testid="logout-btn-desktop"
          >
            <SignOut size={14} weight="bold" /> Logout
          </button>
        </div>
      </header>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 h-16 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 flex items-stretch">
        {mobileTabs.map((t) => {
          const locked = t.vendorOnly && !isVendor;
          const target = locked ? "/account?upgrade=1" : t.to;
          return (
            <NavLink
              key={t.to}
              to={target}
              data-testid={t.testId}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors relative ${
                  isActive && !locked ? "text-[#FF5F1F]" : locked ? "text-zinc-600" : "text-zinc-500"
                }`
              }
            >
              <t.icon size={20} weight="bold" />
              {locked && (
                <LockKey size={9} weight="fill" className="absolute top-2 right-[35%] text-[#FFD700]" />
              )}
              <span className="font-display text-[9px] tracking-widest uppercase font-bold">{t.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </>
  );
}
